(() => {
  'use strict';

  const d = document;
  const w = window;
  const $ = (selector, context = d) => context.querySelector(selector);
  const $$ = (selector, context = d) => Array.from(context.querySelectorAll(selector));
  const reducedMotion = w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const keys = {
    location: 'kloudz_delivery_preference_v1',
    saved: 'kloudz_saved_products_v1',
    compare: 'kloudz_compare_products_v1',
    recent: 'klz_recent_products_v1'
  };

  const read = (key, fallback = []) => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      return value === null ? fallback : value;
    } catch (_) {
      return fallback;
    }
  };
  const write = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_) {
      return false;
    }
  };
  const clean = (value) => String(value == null ? '' : value).trim();
  const currentCurrency = w.Shopify && w.Shopify.currency && w.Shopify.currency.active ? w.Shopify.currency.active : 'USD';
  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(d.documentElement.lang || 'en-US', { style: 'currency', currency: currentCurrency }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  };

  let toastTimer;
  const toast = (message) => {
    const node = $('[data-kzc-toast]');
    if (!node) return;
    clearTimeout(toastTimer);
    node.textContent = message;
    node.classList.add('is-visible');
    toastTimer = setTimeout(() => node.classList.remove('is-visible'), 2600);
  };

  /* Accessible modal orchestration */
  let returnFocus = null;
  const modalFor = (name) => $(`[data-kzc-modal="${name}"]`);
  const focusable = (root) => $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', root)
    .filter((element) => !element.hidden && element.offsetParent !== null);
  const closeModals = () => {
    $$('[data-kzc-modal]').forEach((modal) => {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
    });
    d.documentElement.classList.remove('kzc-no-scroll');
    if (returnFocus && d.contains(returnFocus)) setTimeout(() => returnFocus.focus(), 0);
  };
  const openModal = (name, trigger) => {
    const modal = modalFor(name);
    if (!modal) return;
    closeModals();
    returnFocus = trigger || d.activeElement;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    d.documentElement.classList.add('kzc-no-scroll');
    setTimeout(() => {
      const list = focusable(modal);
      (list[0] || modal).focus?.();
    }, 20);
  };
  d.addEventListener('keydown', (event) => {
    const visible = $$('[data-kzc-modal]').find((modal) => !modal.hidden);
    if (!visible) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeModals();
      return;
    }
    if (event.key !== 'Tab') return;
    const list = focusable(visible);
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    if (event.shiftKey && d.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && d.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Delivery preference: lightweight, private and device-local. */
  const locationMap = {
    shipping: { mini: 'Delivery preference', label: 'Ship to you' },
    lansing: { mini: 'Shop locally', label: 'Lansing' },
    belleville: { mini: 'Shop locally', label: 'Belleville' }
  };
  const getLocation = () => {
    const stored = read(keys.location, 'shipping');
    return locationMap[stored] ? stored : 'shipping';
  };
  const renderLocation = () => {
    const value = getLocation();
    const copy = locationMap[value];
    $$('[data-kzc-location-trigger]').forEach((button) => {
      const mini = $('[data-kzc-location-mini]', button);
      const label = $('[data-kzc-location-label]', button);
      if (mini) mini.textContent = copy.mini;
      if (label) label.textContent = copy.label;
    });
    $$('[data-kzc-location-value]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.kzcLocationValue === value)));
  };
  renderLocation();

  /* Live catalog predictive search with keyboard navigation. */
  const searchInput = $('[data-kz-search-input]');
  const searchForm = $('[data-kz-search-form]');
  const searchAssist = $('[data-kz-search-assist]');
  let searchController;
  let searchTimer;
  let activeSuggestion = -1;
  const initialAssist = searchAssist ? searchAssist.cloneNode(true) : null;
  const createText = (tag, className, text) => {
    const node = d.createElement(tag);
    if (className) node.className = className;
    node.textContent = clean(text);
    return node;
  };
  const productResult = (product, index) => {
    const link = d.createElement('a');
    link.className = 'kzc-predictive-item';
    link.href = product.url;
    link.id = `KzcSuggestion${index}`;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    const media = d.createElement('span');
    media.className = 'kzc-predictive-media';
    const imageUrl = product.featured_image && (product.featured_image.url || product.featured_image);
    if (imageUrl) {
      const image = d.createElement('img');
      image.src = imageUrl;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.appendChild(image);
    }
    const copy = d.createElement('span');
    copy.className = 'kzc-predictive-copy';
    copy.append(createText('strong', '', product.title), createText('span', '', product.vendor || product.type || 'Product'));
    const price = createText('span', 'kzc-predictive-price', typeof product.price === 'number' ? formatMoney(product.price) : product.price || '');
    link.append(media, copy, price);
    return link;
  };
  const simpleResult = (result, label, index) => {
    const link = d.createElement('a');
    link.className = 'kzc-predictive-item';
    link.href = result.url || `${w.routes?.search_url || '/search'}?type=product&q=${encodeURIComponent(result.text || result.title || '')}`;
    link.id = `KzcSuggestion${index}`;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    const media = createText('span', 'kzc-predictive-media', label === 'Collections' ? 'COL' : '⌕');
    media.style.display = 'grid';
    media.style.placeItems = 'center';
    media.style.fontSize = '10px';
    media.style.fontWeight = '900';
    const copy = d.createElement('span');
    copy.className = 'kzc-predictive-copy';
    copy.append(createText('strong', '', result.title || result.text), createText('span', '', label.slice(0, -1)));
    link.append(media, copy, createText('span', 'kzc-predictive-price', '→'));
    return link;
  };
  const renderPredictive = (data, query) => {
    if (!searchAssist) return;
    const results = data && data.resources && data.resources.results ? data.resources.results : {};
    const products = Array.isArray(results.products) ? results.products : [];
    const collections = Array.isArray(results.collections) ? results.collections : [];
    const queries = Array.isArray(results.queries) ? results.queries : [];
    searchAssist.replaceChildren();
    const wrap = d.createElement('div');
    wrap.className = 'kzc-predictive';
    wrap.setAttribute('role', 'listbox');
    let index = 0;
    const addGroup = (label, values, renderer) => {
      if (!values.length) return;
      const group = d.createElement('div');
      group.className = 'kzc-predictive-group';
      group.appendChild(createText('span', 'kzc-predictive-label', label));
      values.forEach((value) => group.appendChild(renderer(value, index++)));
      wrap.appendChild(group);
    };
    addGroup('Products', products.slice(0, 6), (value, i) => productResult(value, i));
    addGroup('Collections', collections.slice(0, 3), (value, i) => simpleResult(value, 'Collections', i));
    addGroup('Search ideas', queries.slice(0, 3), (value, i) => simpleResult(value, 'Search ideas', i));
    if (!index) wrap.appendChild(createText('div', 'kzc-predictive-status', `No instant matches for “${query}”. Press Enter to search the full catalog.`));
    const all = d.createElement('a');
    all.className = 'kzc-predictive-all';
    all.href = `${w.routes?.search_url || '/search'}?type=product&q=${encodeURIComponent(query)}`;
    all.append(createText('span', '', `Search all results for “${query}”`), createText('span', '', '→'));
    wrap.appendChild(all);
    searchAssist.appendChild(wrap);
    searchAssist.hidden = false;
    activeSuggestion = -1;
  };
  const loadPredictive = (query) => {
    clearTimeout(searchTimer);
    const term = clean(query);
    if (!term) {
      if (searchAssist && initialAssist) {
        searchAssist.replaceChildren(...Array.from(initialAssist.childNodes).map((node) => node.cloneNode(true)));
        searchAssist.hidden = false;
      }
      return;
    }
    if (term.length < 2) return;
    searchTimer = setTimeout(async () => {
      if (searchController) searchController.abort();
      searchController = new AbortController();
      if (searchAssist) {
        searchAssist.hidden = false;
        searchAssist.replaceChildren(createText('div', 'kzc-predictive-status', 'Finding the best matches…'));
      }
      try {
        const endpoint = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,collection,query&resources[limit]=8&resources[options][unavailable_products]=last`;
        const response = await fetch(endpoint, { headers: { Accept: 'application/json' }, signal: searchController.signal });
        if (!response.ok) throw new Error('predictive-search');
        renderPredictive(await response.json(), term);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        if (searchAssist) searchAssist.replaceChildren(createText('div', 'kzc-predictive-status', 'Instant results are unavailable. Press Enter to search the full catalog.'));
      }
    }, 180);
  };
  if (searchInput && searchAssist) {
    searchInput.setAttribute('aria-autocomplete', 'list');
    searchInput.setAttribute('aria-controls', 'KzcPredictiveResults');
    searchAssist.id = 'KzcPredictiveResults';
    searchInput.addEventListener('input', () => loadPredictive(searchInput.value));
    searchInput.addEventListener('focus', () => loadPredictive(searchInput.value));
    searchInput.addEventListener('keydown', (event) => {
      const options = $$('.kzc-predictive-item', searchAssist);
      if (event.key === 'Escape') {
        searchAssist.hidden = true;
        activeSuggestion = -1;
        return;
      }
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      if (event.key === 'Enter' && activeSuggestion >= 0) {
        event.preventDefault();
        options[activeSuggestion].click();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        activeSuggestion = event.key === 'ArrowDown'
          ? (activeSuggestion + 1) % options.length
          : (activeSuggestion - 1 + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === activeSuggestion)));
        searchInput.setAttribute('aria-activedescendant', options[activeSuggestion].id);
        options[activeSuggestion].scrollIntoView({ block: 'nearest' });
      }
    });
  }

  /* Optional voice input. It never submits automatically. */
  const Recognition = w.SpeechRecognition || w.webkitSpeechRecognition;
  if (Recognition && searchForm && searchInput) {
    let voiceButton = $('[data-kzc-voice]', searchForm);
    if (!voiceButton) {
      voiceButton = d.createElement('button');
      voiceButton.type = 'button';
      voiceButton.className = 'kzc-voice-search';
      voiceButton.dataset.kzcVoice = '';
      voiceButton.setAttribute('aria-label', 'Search by voice');
      voiceButton.setAttribute('aria-pressed', 'false');
      voiceButton.textContent = '◉';
      const submit = $('.kz-market-search-button', searchForm);
      searchForm.insertBefore(voiceButton, submit);
    }
    voiceButton.addEventListener('click', () => {
      const recognition = new Recognition();
      recognition.lang = d.documentElement.lang || 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      voiceButton.setAttribute('aria-pressed', 'true');
      toast('Listening… say a product, brand or category.');
      recognition.onresult = (event) => {
        searchInput.value = event.results[0][0].transcript;
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        searchInput.focus();
        toast('Voice search captured. Review it, then press Search.');
      };
      recognition.onerror = () => toast('Voice search could not start. You can keep typing instead.');
      recognition.onend = () => voiceButton.setAttribute('aria-pressed', 'false');
      try { recognition.start(); } catch (_) { voiceButton.setAttribute('aria-pressed', 'false'); }
    });
  }

  /* Product context, saved items and compare tray. */
  const parseContext = (selector) => {
    const node = $(selector);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (_) { return null; }
  };
  const currentProduct = parseContext('#KzcProductContext') || parseContext('#KlzProductContext');
  const normalizeItem = (item) => item && item.url ? {
    id: clean(item.id || item.url),
    title: clean(item.title || 'View product'),
    url: clean(item.url),
    image: clean(item.image),
    price: clean(item.price),
    type: clean(item.type || 'Product'),
    vendor: clean(item.vendor),
    available: item.available !== false
  } : null;
  const savedItems = () => {
    const value = read(keys.saved, []);
    return Array.isArray(value) ? value.map(normalizeItem).filter(Boolean) : [];
  };
  const compareItems = () => {
    const value = read(keys.compare, []);
    return Array.isArray(value) ? value.map(normalizeItem).filter(Boolean).slice(0, 4) : [];
  };
  const isSame = (left, right) => clean(left.id || left.url) === clean(right.id || right.url);
  const itemFromCard = (card) => {
    if (!card) return null;
    const link = $('a[href*="/products/"]', card);
    if (!link) return null;
    const image = $('img', card);
    const titleNode = $('.kzc-product-title,.kz-market-product-title,.card-title,.product-title,[class*="card-title"]', card) || link;
    const priceNode = $('.kzc-product-price,.kz-market-product-price,.price,.price-item,[class*="price"]', card);
    return normalizeItem({
      id: card.dataset.kzcProductId || card.dataset.productId || link.getAttribute('href'),
      title: card.dataset.kzcProductTitle || titleNode.textContent,
      url: card.dataset.kzcProductUrl || link.href,
      image: card.dataset.kzcProductImage || (image && (image.currentSrc || image.src)),
      price: card.dataset.kzcProductPrice || (priceNode && priceNode.textContent.replace(/\s+/g, ' ').trim()),
      type: card.dataset.kzcProductType || 'Product',
      available: card.dataset.kzcProductAvailable !== 'false'
    });
  };
  const updateProductButtons = () => {
    const saved = savedItems();
    const compared = compareItems();
    $$('[data-kzc-save-product]').forEach((button) => {
      const item = currentProduct || itemFromCard(button.closest('[data-kzc-product-card],.kzc-product,.kz-market-product-card,.card,.product-card,.product-item'));
      const pressed = item ? saved.some((savedItem) => isSame(savedItem, item)) : false;
      button.setAttribute('aria-pressed', String(pressed));
      const label = $('[data-kzc-save-label]', button);
      if (label) label.textContent = pressed ? 'Saved' : 'Save for later';
    });
    $$('[data-kzc-compare-product],[data-kzc-compare-card],.kzc-card-compare').forEach((button) => {
      const item = currentProduct || itemFromCard(button.closest('[data-kzc-product-card],.kzc-product,.kz-market-product-card,.card,.product-card,.product-item'));
      button.setAttribute('aria-pressed', String(item ? compared.some((comparedItem) => isSame(comparedItem, item)) : false));
    });
  };
  const renderSaved = () => {
    const grid = $('[data-kzc-saved-grid]');
    const empty = $('[data-kzc-saved-empty]');
    if (!grid) return;
    const items = savedItems();
    grid.replaceChildren();
    items.forEach((item) => {
      const row = d.createElement('article');
      row.className = 'kzc-saved-item';
      const image = d.createElement('img');
      image.src = item.image || '';
      image.alt = '';
      image.loading = 'lazy';
      const copy = d.createElement('div');
      copy.className = 'kzc-saved-copy';
      const link = d.createElement('a');
      link.href = item.url;
      link.textContent = item.title;
      copy.append(link, createText('span', '', item.price || item.type));
      const remove = d.createElement('button');
      remove.className = 'kzc-saved-remove';
      remove.type = 'button';
      remove.dataset.kzcRemoveSaved = item.id;
      remove.setAttribute('aria-label', `Remove ${item.title} from saved items`);
      remove.textContent = '×';
      row.append(image, copy, remove);
      grid.appendChild(row);
    });
    if (empty) empty.hidden = items.length > 0;
    grid.hidden = items.length === 0;
  };
  const renderCompareTray = () => {
    const items = compareItems();
    const tray = $('[data-kzc-compare-tray]');
    const count = $('[data-kzc-compare-count]');
    if (count) count.textContent = String(items.length);
    if (tray) tray.classList.toggle('is-visible', items.length > 0);
    const copy = $('[data-kzc-compare-copy]');
    if (copy) copy.textContent = items.length === 1 ? 'Add another product to compare details.' : `${items.length} products ready to compare.`;
    updateProductButtons();
  };
  const renderCompareTable = () => {
    const target = $('[data-kzc-compare-table]');
    const empty = $('[data-kzc-compare-empty]');
    if (!target) return;
    const items = compareItems();
    target.replaceChildren();
    if (empty) empty.hidden = items.length > 0;
    target.hidden = items.length === 0;
    if (!items.length) return;
    const table = d.createElement('table');
    table.className = 'kzc-compare-table';
    const rows = [
      ['Product', ...items.map((item) => ({ type: 'product', item }))],
      ['Price', ...items.map((item) => item.price || 'See product')],
      ['Category', ...items.map((item) => item.type || 'Product')],
      ['Availability', ...items.map((item) => item.available ? 'Available' : 'Currently unavailable')]
    ];
    rows.forEach((rowData, rowIndex) => {
      const row = d.createElement('tr');
      const label = d.createElement(rowIndex === 0 ? 'th' : 'td');
      label.textContent = rowData[0];
      row.appendChild(label);
      rowData.slice(1).forEach((cellData) => {
        const cell = d.createElement(rowIndex === 0 ? 'th' : 'td');
        if (cellData && cellData.type === 'product') {
          const item = cellData.item;
          if (item.image) {
            const image = d.createElement('img');
            image.src = item.image;
            image.alt = '';
            image.loading = 'lazy';
            cell.appendChild(image);
          }
          const link = d.createElement('a');
          link.href = item.url;
          link.textContent = item.title;
          cell.appendChild(link);
        } else {
          cell.textContent = clean(cellData);
        }
        row.appendChild(cell);
      });
      table.appendChild(row);
    });
    target.appendChild(table);
  };
  const toggleSaved = (item) => {
    const normalized = normalizeItem(item);
    if (!normalized) return;
    const items = savedItems();
    const exists = items.some((savedItem) => isSame(savedItem, normalized));
    const next = exists ? items.filter((savedItem) => !isSame(savedItem, normalized)) : [normalized, ...items].slice(0, 24);
    write(keys.saved, next);
    renderSaved();
    updateProductButtons();
    toast(exists ? 'Removed from saved items.' : 'Saved for later on this device.');
  };
  const toggleCompare = (item) => {
    const normalized = normalizeItem(item);
    if (!normalized) return;
    const items = compareItems();
    const exists = items.some((comparedItem) => isSame(comparedItem, normalized));
    let next;
    if (exists) {
      next = items.filter((comparedItem) => !isSame(comparedItem, normalized));
      toast('Removed from comparison.');
    } else if (items.length >= 4) {
      toast('Compare up to four products at a time.');
      return;
    } else {
      next = [...items, normalized];
      toast('Added to comparison.');
    }
    write(keys.compare, next);
    renderCompareTray();
    renderCompareTable();
  };

  /* Add unobtrusive compare controls to existing theme product cards. */
  const enhanceCards = () => {
    $$('.card,.product-card,.product-item,.kz-market-product-card').forEach((card) => {
      if ($('[data-kzc-compare-card],.kzc-card-compare', card) || !itemFromCard(card)) return;
      const button = d.createElement('button');
      button.type = 'button';
      button.className = 'kzc-card-compare';
      button.dataset.kzcCompareCard = '';
      button.setAttribute('aria-label', 'Add product to comparison');
      button.setAttribute('aria-pressed', 'false');
      button.textContent = '⇄';
      card.appendChild(button);
    });
    updateProductButtons();
  };

  /* Recently viewed becomes an on-home inspired rail. */
  const renderInspired = () => {
    const rail = $('[data-kzc-inspired-grid]');
    const empty = $('[data-kzc-inspired-empty]');
    if (!rail) return;
    const recent = read(keys.recent, []);
    const items = Array.isArray(recent) ? recent.map(normalizeItem).filter(Boolean).slice(0, 8) : [];
    rail.replaceChildren();
    items.forEach((item) => {
      const card = d.createElement('article');
      card.className = 'kzc-product';
      card.dataset.kzcProductCard = '';
      card.dataset.kzcProductId = item.id;
      card.dataset.kzcProductTitle = item.title;
      card.dataset.kzcProductUrl = item.url;
      card.dataset.kzcProductImage = item.image;
      card.dataset.kzcProductPrice = item.price;
      card.dataset.kzcProductType = item.type;
      const media = d.createElement('a');
      media.className = 'kzc-product-media';
      media.href = item.url;
      if (item.image) {
        const image = d.createElement('img');
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        image.decoding = 'async';
        media.appendChild(image);
      }
      const body = d.createElement('div');
      body.className = 'kzc-product-body';
      const title = d.createElement('a');
      title.className = 'kzc-product-title';
      title.href = item.url;
      title.textContent = item.title;
      const price = createText('div', 'kzc-product-price', item.price);
      const stock = createText('div', 'kzc-product-stock', 'Continue shopping');
      const actions = d.createElement('div');
      actions.className = 'kzc-product-actions';
      const view = d.createElement('a');
      view.href = item.url;
      view.textContent = 'View product';
      const compare = d.createElement('button');
      compare.type = 'button';
      compare.dataset.kzcCompareCard = '';
      compare.setAttribute('aria-label', `Compare ${item.title}`);
      compare.textContent = '⇄';
      actions.append(view, compare);
      body.append(title, price, stock, actions);
      card.append(media, body);
      rail.appendChild(card);
    });
    rail.hidden = items.length === 0;
    if (empty) empty.hidden = items.length > 0;
    enhanceCards();
  };

  /* Cart shipping-goal feedback based on the store's U.S. $200 threshold. */
  const updateCartGoal = (cart) => {
    const node = $('[data-kzc-cart-goal]');
    if (!node || !cart) return;
    const threshold = Number(node.dataset.kzcThreshold || 20000);
    const total = Number(cart.total_price || 0);
    const remaining = Math.max(0, threshold - total);
    const percentage = Math.min(100, threshold ? (total / threshold) * 100 : 100);
    const message = $('[data-kzc-cart-goal-message]', node);
    const amount = $('[data-kzc-cart-goal-amount]', node);
    const progress = $('[data-kzc-cart-goal-progress]', node);
    node.hidden = Number(cart.item_count || 0) < 1;
    node.classList.toggle('is-complete', remaining === 0);
    if (message) message.textContent = remaining === 0 ? 'Free U.S. shipping goal reached' : 'Free U.S. shipping goal';
    if (amount) amount.textContent = remaining === 0 ? 'Qualified' : `${formatMoney(remaining)} away`;
    if (progress) progress.style.width = `${percentage}%`;
  };
  const refreshCartGoal = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (response.ok) updateCartGoal(await response.json());
    } catch (_) {}
  };
  const cartContext = parseContext('#KzcCartContext');
  if (cartContext) updateCartGoal(cartContext);

  /* Move page-specific helpers into their natural location after native sections render. */
  const intelligence = $('[data-kzc-product-intelligence]');
  if (intelligence) {
    const target = $('.productView-details,.product__info-container,.productView-right,.halo-productView-right');
    if (target) target.appendChild(intelligence);
  }
  const cartGoal = $('[data-kzc-cart-goal]');
  if (cartGoal) {
    const cartTarget = $('.cart__footer,.cart-total,.halo-cart-sidebar .previewCart,.cart-sidebar .previewCart');
    if (cartTarget) cartTarget.prepend(cartGoal);
  }

  d.addEventListener('click', (event) => {
    const open = event.target.closest('[data-kzc-open]');
    if (open) {
      event.preventDefault();
      const name = open.dataset.kzcOpen;
      if (name === 'saved') renderSaved();
      if (name === 'compare') renderCompareTable();
      openModal(name, open);
      return;
    }
    if (event.target.closest('[data-kzc-close]')) {
      event.preventDefault();
      closeModals();
      return;
    }
    const location = event.target.closest('[data-kzc-location-value]');
    if (location) {
      write(keys.location, location.dataset.kzcLocationValue);
      renderLocation();
      closeModals();
      toast(`Shopping preference set to ${locationMap[getLocation()].label}.`);
      return;
    }
    const save = event.target.closest('[data-kzc-save-product]');
    if (save) {
      event.preventDefault();
      const item = currentProduct || itemFromCard(save.closest('[data-kzc-product-card],.kzc-product,.kz-market-product-card,.card,.product-card,.product-item'));
      toggleSaved(item);
      return;
    }
    const compare = event.target.closest('[data-kzc-compare-product],[data-kzc-compare-card],.kzc-card-compare');
    if (compare) {
      event.preventDefault();
      const item = currentProduct || itemFromCard(compare.closest('[data-kzc-product-card],.kzc-product,.kz-market-product-card,.card,.product-card,.product-item'));
      toggleCompare(item);
      return;
    }
    const removeSaved = event.target.closest('[data-kzc-remove-saved]');
    if (removeSaved) {
      const next = savedItems().filter((item) => clean(item.id) !== clean(removeSaved.dataset.kzcRemoveSaved));
      write(keys.saved, next);
      renderSaved();
      updateProductButtons();
      toast('Removed from saved items.');
      return;
    }
    if (event.target.closest('[data-kzc-clear-compare]')) {
      write(keys.compare, []);
      renderCompareTray();
      renderCompareTable();
      toast('Comparison cleared.');
    }
  });

  d.addEventListener('submit', (event) => {
    if (event.target.matches('form[action*="/cart/add"]')) setTimeout(refreshCartGoal, 700);
  });
  ['cart:updated', 'cart:refresh', 'cart:change', 'ajaxProduct:added'].forEach((name) => d.addEventListener(name, refreshCartGoal));
  w.addEventListener('pageshow', () => {
    renderLocation();
    renderSaved();
    renderCompareTray();
    renderInspired();
  });
  d.addEventListener('shopify:section:load', (event) => {
    enhanceCards();
    renderInspired();
    if (event.target && event.target.querySelector('[data-kzc-cart-goal]')) refreshCartGoal();
  });

  renderSaved();
  renderCompareTray();
  renderCompareTable();
  renderInspired();
  enhanceCards();
  d.documentElement.classList.add('kzc-ready');
})();
